import React, { useState } from 'react';
import styled from 'styled-components';
import { FaPaperPlane } from 'react-icons/fa';
import useForm from '../../hooks/useForm';
import Button from '../common/Button';
import contactService from '../../services/contactService';
import SectionHeader from '../common/SectionHeader';

const FormSection = styled.section`
  background-color: ${props => props.theme.colors.lightColor};
  padding: 80px 0;
`;

const FormContainer = styled.div`
  display: flex;
  gap: 50px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const FormWrapper = styled.div`
  flex: 1;
`;

const FormHeader = styled.div`
  margin-bottom: 30px;
`;

const FormTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xxlarge};
  margin-bottom: 10px;
  color: ${props => props.theme.colors.secondaryColor};
`;

const FormDescription = styled.p`
  color: ${props => props.theme.colors.grey[600]};
`;

const StyledForm = styled.form`
  background-color: ${props => props.theme.colors.white};
  padding: 30px;
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadows.regular};
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0;
  }
`;

const FormGroup = styled.div`
  flex: 1;
  margin-bottom: ${props => props.bottomMargin || '20px'};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${props => props.theme.colors.secondaryColor};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.grey[300]};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.main};
  
  &:focus {
    border-color: ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.primaryColor};
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.grey[300]};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.main};
  resize: vertical;
  min-height: 150px;
  
  &:focus {
    border-color: ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.primaryColor};
    outline: none;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.grey[300]};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.main};
  background-color: white;
  
  &:focus {
    border-color: ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.primaryColor};
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.dangerColor};
  font-size: ${props => props.theme.fontSizes.small};
  margin-top: 5px;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.successColor};
  background-color: rgba(40, 167, 69, 0.1);
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
    font-size: 1.2rem;
  }
`;

const FormImage = styled.div`
  flex: 1;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const ContactForm = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Form validation function
  const validateForm = (values) => {
    const errors = {};
    
    if (!values.name || values.name.trim() === '') {
      errors.name = 'Name is required';
    }
    
    if (!values.email || values.email.trim() === '') {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!values.phone || values.phone.trim() === '') {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[0-9]{10,15}$/.test(values.phone.replace(/\s/g, ''))) {
      errors.phone = 'Phone number is invalid';
    }
    
    if (!values.subject || values.subject.trim() === '') {
      errors.subject = 'Subject is required';
    }
    
    if (!values.message || values.message.trim() === '') {
      errors.message = 'Message is required';
    }
    
    return errors;
  };
  
  // Form submission handler
  const handleFormSubmit = async (values) => {
    try {
      await contactService.submitContactForm(values);
      setFormSubmitted(true);
      resetForm();
      
      // Reset form submission status after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      throw new Error(error.response?.data?.message || 'Failed to submit the form. Please try again.');
    }
  };
  
  // Initialize useForm hook
  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm
  } = useForm(
    {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      inquiry: 'general'
    },
    validateForm,
    handleFormSubmit
  );
  
  return (
    <FormSection>
      <div className="container">
        <SectionHeader 
          title="Send Us a Message" 
          subtitle="Fill out the form below and we'll get back to you as soon as possible" 
        />
        
        <FormContainer>
          <FormWrapper>
            {formSubmitted && (
              <SuccessMessage>
                <FaPaperPlane />
                <span>Thank you for your message! We'll get back to you as soon as possible.</span>
              </SuccessMessage>
            )}
            
            <StyledForm onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="name">Your Name*</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && errors.name}
                  />
                  {touched.name && errors.name && (
                    <ErrorMessage>{errors.name}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email">Your Email*</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && errors.email}
                  />
                  {touched.email && errors.email && (
                    <ErrorMessage>{errors.email}</ErrorMessage>
                  )}
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label htmlFor="phone">Phone Number*</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.phone && errors.phone}
                  />
                  {touched.phone && errors.phone && (
                    <ErrorMessage>{errors.phone}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="subject">Subject*</Label>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={values.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.subject && errors.subject}
                  />
                  {touched.subject && errors.subject && (
                    <ErrorMessage>{errors.subject}</ErrorMessage>
                  )}
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <Label htmlFor="message">Your Message*</Label>
                <Textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={values.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.message && errors.message}
                />
                {touched.message && errors.message && (
                  <ErrorMessage>{errors.message}</ErrorMessage>
                )}
              </FormGroup>
              
              <FormGroup bottomMargin="30px">
                <Label htmlFor="inquiry">Inquiry Type</Label>
                <Select
                  id="inquiry"
                  name="inquiry"
                  value={values.inquiry}
                  onChange={handleChange}
                >
                  <option value="general">General Inquiry</option>
                  <option value="admission">Admission Inquiry</option>
                  <option value="tnpsc">TNPSC Exam Coaching</option>
                  <option value="other">Other</option>
                </Select>
              </FormGroup>
              
              {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}
              
              <Button 
                type="submit" 
                variant="primary" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </StyledForm>
          </FormWrapper>
          
          <FormImage>
            <img src="/placeholder/500/600" alt="Contact KALS Academy" />
          </FormImage>
        </FormContainer>
      </div>
    </FormSection>
  );
};

export default ContactForm;
